import { Component, computed, DestroyRef, effect, inject, input, OnInit, signal, viewChild } from '@angular/core';
import {
  AriaLiveComponent,
  ChatbotComponent,
  CookieComponent,
  CookieStore,
  GlobalNotificationWrapperComponent,
  HeaderComponent,
  HeadlineService,
  NavbarFooterComponent,
  NavbarHeaderComponent,
  PageRibbonComponent,
  ToastComponent,
} from '@mwe/ui/components';
import dayjs from 'dayjs';
import { from, fromEvent, merge, switchMap, tap } from 'rxjs';
import { ActivatedRoute, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { AccountLogic, ChatBotService, EnvironmentService, PopupService, ProductLogic, ProfileService } from '@mwe/services';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { IdleCheckService } from '@mwe/interceptors';
import { FEATURE_TOGGLED_OUT_ROUTE, FeatureToggleEnum, LANDING_PAGE_B2C_REPLACEMENT_ROUTE } from '@mwe/constants';
import { getRouteData, track } from '@mwe/utils';
import { DOCUMENT, NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mwe-app-base',
  templateUrl: './app-base.component.html',
  imports: [
    PageRibbonComponent,
    NgClass,
    HeaderComponent,
    NavbarHeaderComponent,
    RouterOutlet,
    ChatbotComponent,
    NavbarFooterComponent,
    AriaLiveComponent,
    ToastComponent,
    GlobalNotificationWrapperComponent,
  ],
  providers: [HeadlineService],
})
export class AppBaseComponent implements OnInit {
  chatbotRef = viewChild(ChatbotComponent);
  defaultPageTitle = input<string>('');

  showNavBar = signal(false);
  hideFooter = signal(false);
  noBottomPadding = signal(false);
  isCookieConsentEnabled = signal(false);
  isMaintenanceEnabled = signal(false);
  routeLoaded = signal(false);
  showChatbot = signal(false);
  time = signal<any>(null);
  lastTimeWhenWindowWasFocused = signal<dayjs.Dayjs>(null);

  private readonly cookieStore = inject(CookieStore);
  private acceptedVersion = computed(() => this.cookieStore.acceptedVersion());
  private version = computed(() => this.cookieStore.version());
  private data = computed(() => this.cookieStore.data());

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private profileService = inject(ProfileService);
  private translateService = inject(TranslateService);
  private titleService = inject(Title);
  private accountLogic = inject(AccountLogic);
  private chatbotService = inject(ChatBotService);
  private idleCheckService = inject(IdleCheckService);
  private productLogic = inject(ProductLogic);
  private envService = inject(EnvironmentService);

  private popupService = inject(PopupService);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  constructor() {
    effect(() => {
      this.checkAndDisplayCookieConsent();
    });

    this.setupIdleTimer();
    this.setupWindowFocusBlur();
  }

  ngOnInit() {
    this.isCookieConsentEnabled.set(
      this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.COOKIE_POPUP_ENABLED) && !this.accountLogic.isImpersonate(),
    );

    this.chatbotService
      .events()
      ?.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(value => {
          if (this.showChatbot() && value) {
            this.chatbotRef().showChatbot();
          }
          this.showChatbot.set(value);
        }),
      )
      ?.subscribe();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(event => {
          if (event instanceof NavigationEnd) {
            const routeData = getRouteData(this.activatedRoute) || {};
            const titles = routeData.pageTitles || [this.defaultPageTitle()];
            this.showNavBar.set(routeData.showNavBar);
            this.hideFooter.set(routeData.hideFooter);
            this.noBottomPadding.set(routeData.noBottomPadding);

            this.updateTitleFromKeys(titles);

            track('virtualPageView', {
              title: document.title,
              url: location.pathname,
            });

            this.isMaintenanceEnabled.set(
              this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.MAINTENANCE_ENABLED) ||
                event.url === `/${LANDING_PAGE_B2C_REPLACEMENT_ROUTE}` ||
                event.url === `/${FEATURE_TOGGLED_OUT_ROUTE}`,
            );
            this.routeLoaded.set(true);
          }
          if (event instanceof NavigationError && event.error.status === 404) {
            this.router.navigate(['/404']);
          }
        }),
      )
      .subscribe();

    if (this.accountLogic.isLoggedIn()) {
      this.productLogic
        .prepareProducts({ isRefresh: true })
        .pipe(
          tap(() => this.productLogic.setInitialFilterState()),
          switchMap(() => from(this.productLogic.checkAssociationsUpdate())),
        )
        .subscribe();
    }
    this.resetIdleTimer();
  }

  private setupIdleTimer() {
    merge(fromEvent(this.document, 'mousemove'), fromEvent(this.document, 'keypress'))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.resetIdleTimer()),
      )
      .subscribe();
  }

  private setupWindowFocusBlur() {
    fromEvent(window, 'focus')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.onFocusWindow()),
      )
      .subscribe();

    fromEvent(window, 'blur')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.onBlurWindow()),
      )
      .subscribe();
  }

  private resetIdleTimer() {
    clearTimeout(this.time());
    this.idleCheckService.setIsUserIdle(false);
    this.time.set(
      setTimeout(() => {
        this.idleCheckService.setIsUserIdle(true);
      }, this.idleCheckService.getTimeWhenUserIsConsideredAsIdle()),
    );
  }

  private onFocusWindow(): void {
    if (!this.lastTimeWhenWindowWasFocused()) {
      return;
    }

    const currentTime = dayjs();
    const lastWindowFocus = this.lastTimeWhenWindowWasFocused();
    const exactTimeWhenUserIsConsideredIdle = lastWindowFocus.add(this.idleCheckService.getTimeWhenUserIsConsideredAsIdle(), 'millisecond');

    if (currentTime.isAfter(exactTimeWhenUserIsConsideredIdle)) {
      this.idleCheckService.setWasWindowInactiveForLongerThanIdleTime(true);
    }
  }

  private onBlurWindow(): void {
    this.lastTimeWhenWindowWasFocused.set(dayjs());
  }

  private checkAndDisplayCookieConsent() {
    if (!this.isCookieConsentEnabled() || !this.data()) return;
    if (this.acceptedVersion() && this.version() && this.acceptedVersion() === this.version()) return;

    this.popupService.openV2({
      id: 'cookieConsent',
      component: CookieComponent,
      dismissable: false,
      titleKey: 'cookie-banner.title',
      modalSize: 'xl',
    });
  }

  private updateTitleFromKeys(titleKeys?: string[], customTitle = '') {
    if (!titleKeys) {
      return;
    }

    const env = this.envService.getEnvironment();

    const postfix =
      env.customerApp == 'privat' ? this.translateService.instant('global.title') : this.translateService.instant('global.title_business');

    this.translateService
      ?.get(titleKeys)
      ?.pipe(
        tap(translationObject => {
          const titles = Object.values(translationObject).reverse();
          if (customTitle) {
            titles.push(`${customTitle} - ${postfix}`);
          }

          const newTitle = titles.join(' - ');
          this.titleService.setTitle(`${newTitle} - ${postfix}`);
        }),
      )
      .subscribe();
  }
}
